import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { EmptyState } from '../../components/ui/PanelCard';
import Modal from '../../components/ui/Modal';
import ConfirmModal from '../../components/ui/ConfirmModal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  ItemsBox, StepsList, KillTargets, itemShort, ago, utcDate, SearchInput, SegFilter, TabHead, revealBelow,
  IconEye, IconPlay, IconCheck, IconFlag,
} from './routeUi';

/**
 * ── LIBRARY REVIEW (csak tester / admin / owner) ────────────────────────────
 *
 * A közösségi könyvtárba csak jóváhagyott route kerül (terv 8.). Itt fogad el /
 * utasít el egy moderátor, és itt rejt el bármit — jelentés nélkül is.
 *
 * MIÉRT A DASHBOARDON, NEM AZ ADMIN PANELBEN: a tester jogosultság-szintje a
 * useré (backend utils/roles.js), az Admin Panelbe nem jut be, és nem is kell.
 *
 * Amit a backend amúgy is kikényszerít, a felület csak tükrözi:
 *   * saját beküldést más hagy jóvá (az owner kivétel);
 *   * végleges törlés csak admin és owner.
 */

const TABS = [
  { id: 'pending', label: 'In review' },
  { id: 'reported', label: 'Reported' },
  { id: 'live', label: 'Live' },
  { id: 'hidden', label: 'Hidden' },
  { id: 'rejected', label: 'Rejected' },
];

export default function ReviewTab({ role, onCounts }) {
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [tab, setTab] = useState('pending');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selVid, setSelVid] = useState(null);
  const [hideTarget, setHideTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  // Gyors fülváltásnál egy korábbi fül késve érkező válasza nem írhatja felül az
  // aktuálisét.
  const tabRef = useRef(tab);
  tabRef.current = tab;

  const load = useCallback(async () => {
    try {
      const res = await api.get('/library/review', { params: { tab } });
      if (tabRef.current !== tab) return;
      setData(res.data);
      setError(null);
      onCounts?.(res.data.counts);
    } catch (err) {
      if (tabRef.current !== tab) return;
      const msg = err.response?.data?.error || 'Could not load the review queue.';
      setError({ tab, msg });
      toastRef.current.error(msg);
    }
  }, [tab, onCounts]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelVid(null); }, [tab]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toastRef.current.success(okMsg);
      await load();
      return true;
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Something went wrong.');
      await load();
      return false;
    } finally {
      setBusy(false);
    }
  };

  // CSAK AZ ÉPP KÉRT FÜL VÁLASZA rajzolható: fülváltáskor a régi válasz még itt
  // van, és más alakú (egy várakozó beküldés nem listing-sor) — régen ebből lett
  // "Cannot read properties of undefined" a Reported fülön.
  const shown = data && data.tab === tab ? data : null;
  const items = (shown?.items || []).filter(i => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (i.title || '').toLowerCase().includes(s) || (i.author || '').toLowerCase().includes(s);
  });
  const counts = data?.counts || {};
  const canRemove = !!data?.me?.canRemove;

  return (
    <div className="flex flex-col gap-6">
      <TabHead title="Library review" right={<span className="badge-brand">You review as {role}</span>}>
        Nothing reaches the route library until a tester, an admin or the owner approves it. Only these roles see this page.
      </TabHead>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegFilter
          label="Review lists"
          value={tab}
          onChange={setTab}
          items={TABS.map(t => ({
            ...t,
            count: counts[t.id],
            hot: (t.id === 'pending' || t.id === 'reported') && counts[t.id] > 0 && tab !== t.id,
          }))}
        />
        <SearchInput value={q} onChange={setQ} placeholder="Search title or author" className="w-full sm:w-[260px]" />
      </div>

      {!shown ? (
        error?.tab === tab
          ? <div className="card p-6 text-sm text-[#ff8a80]">{error.msg}</div>
          : <div className="card py-12 flex justify-center"><LoadingSpinner label="Loading…" /></div>
      ) : tab === 'pending' ? (
        <PendingView items={items} selVid={selVid} onSelect={setSelVid} busy={busy}
                     selfApprove={data?.me?.role === 'owner'}
                     onApprove={(vid) => act(() => api.post(`/library/review/version/${vid}/approve`), 'Approved — it is in the library now.').then(ok => ok && setSelVid(null))}
                     onReject={(vid, reason, isPublic) => act(() => api.post(`/library/review/version/${vid}/reject`, { reason, public: isPublic }), 'Rejected.').then(ok => ok && setSelVid(null))} />
      ) : tab === 'reported' ? (
        <ListingTable items={items} kind="reported" busy={busy} canRemove={canRemove}
                      onHide={setHideTarget} onRemove={setRemoveTarget}
                      onKeep={(it) => act(() => api.post(`/library/review/${it.listingId}/keep`), 'Reports closed — the listing stays.')} />
      ) : tab === 'live' || tab === 'hidden' ? (
        <ListingTable items={items} kind={tab} busy={busy} canRemove={canRemove}
                      onHide={setHideTarget} onRemove={setRemoveTarget}
                      onUnhide={(it) => act(() => api.post(`/library/review/${it.listingId}/unhide`), 'Visible in the library again.')} />
      ) : (
        <RejectedTable items={items} />
      )}

      {hideTarget && (
        <HideModal target={hideTarget} busy={busy} onClose={() => setHideTarget(null)}
                   onHide={async (reason, isPublic) => {
                     const ok = await act(() => api.post(`/library/review/${hideTarget.listingId}/hide`, { reason, public: isPublic }), 'Hidden from the library.');
                     if (ok) setHideTarget(null);
                   }} />
      )}
      {removeTarget && (
        <ConfirmModal
          title="Remove this listing for good?"
          message={`"${removeTarget.title}" by ${removeTarget.author} is deleted from the library. Players who already added it keep their own copy. This cannot be undone — use Hide if you are not sure.`}
          confirmLabel="Remove for good"
          danger
          onCancel={() => setRemoveTarget(null)}
          onConfirm={async () => {
            const t = removeTarget;
            setRemoveTarget(null);
            await act(() => api.post(`/library/review/${t.listingId}/remove`), 'Removed for good.');
          }}
        />
      )}
    </div>
  );
}

// ── IN REVIEW: SOR + ÁTNÉZÉS ──────────────────────────────────────────────────
// A saját beküldést más hagyja jóvá — az owner kivétel (`selfApprove`, a backend
// reviewVersion canApprove párja).
function PendingView({ items, selVid, onSelect, busy, selfApprove, onApprove, onReject }) {
  const panelRef = useRef(null);
  if (items.length === 0) {
    return (
      <div className="card">
        <EmptyState icon={<path d="M20 6 9 17l-5-5" />} title="Nothing is waiting for review." />
      </div>
    );
  }
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px] items-start">
      <div className="card overflow-hidden">
        <div className="flex justify-between px-5 py-2.5 border-b border-line-faint table-head">
          <span>Waiting · oldest first</span><span>Submitted</span>
        </div>
        {items.map(it => {
          const sel = it.versionId === selVid;
          const items2 = itemShort(it.summary.items);
          return (
            <button key={it.versionId} type="button" onClick={() => { onSelect(it.versionId); revealBelow(panelRef); }}
                    aria-pressed={sel}
                    className={`w-full text-left px-5 py-[11px] border-b border-line-faint flex flex-col gap-1.5 transition-colors ${
                      sel ? 'bg-gray-850 shadow-[inset_2px_0_0_#a48bff]' : 'hover:bg-white/[0.04]'}`}>
              <div className="flex justify-between gap-3">
                <b className="text-sm font-medium text-gray-50 break-words">{it.title}</b>
                <span className="text-[13px] text-gray-400 flex-shrink-0">{ago(it.submittedAt)}</span>
              </div>
              <span className="text-[12.5px] text-gray-500">
                by {it.author}{it.mine ? ' (you)' : ''} · {it.isUpdate ? `update of a live listing (v${it.fromVersion} → v${it.toVersion})` : 'new listing'}
              </span>
              <div className="flex flex-wrap items-center gap-1.5">
                {it.mine && <span className="badge-inactive">Your own</span>}
                {it.isUpdate && <span className="badge-brand">Update</span>}
                <span className="badge-inactive">{it.summary.steps} {it.summary.steps === 1 ? 'step' : 'steps'}</span>
                {items2 ? <span className="badge-warn">{items2}</span> : <span className="badge-active">No items</span>}
                {it.mine && !selfApprove && <span className="text-xs text-gray-500">Another reviewer has to approve it.</span>}
              </div>
            </button>
          );
        })}
      </div>
      <div ref={panelRef} className="scroll-mt-nav min-w-0">
        <ReviewPanel vid={selVid} busy={busy} onApprove={onApprove} onReject={onReject} />
      </div>
    </div>
  );
}

function ReviewPanel({ vid, busy, onApprove, onReject }) {
  const { toast } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [checks, setChecks] = useState({});
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    setReason(''); setIsPublic(true); setChecks({}); setD(null);
    if (!vid) return;
    let alive = true;
    setLoading(true);
    api.get(`/library/review/version/${vid}`)
      .then(res => { if (alive) setD(res.data); })
      .catch(err => toastRef.current.error(err.response?.data?.error || 'Could not load the submission.'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [vid]);

  const tryIt = async () => {
    try {
      const res = await api.post(`/library/review/version/${vid}/try`);
      toastRef.current.success(`A private copy is in your Routes as ${res.data.key}. Your PCs get it on their next sync.`);
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || 'Could not copy it.');
    }
  };

  if (!vid) {
    return <aside className="card px-6 py-10 text-center text-sm text-gray-500">Pick a submission to review it.</aside>;
  }
  if (loading || !d) {
    return <aside className="card p-8 flex justify-center"><LoadingSpinner label="Loading…" /></aside>;
  }

  const s = d.summary;
  return (
    <aside className="card overflow-hidden" aria-label="Review">
      <div className="min-h-[52px] px-5 py-3 flex items-center justify-between gap-3 border-b border-line-faint">
        <span className="panel-label">Review</span>
        <span className="badge-warn">{d.live ? `Update v${d.live.version} → v${d.version}` : 'New listing'}</span>
      </div>

      <div className="px-5 py-4 flex flex-col gap-2">
        <b className="text-lg font-semibold text-gray-50 leading-snug break-words">{d.title}</b>
        <span className="text-xs text-gray-500">
          by <span className="text-gray-100">{d.author}</span> · submitted {ago(d.submittedAt)} · from the author's route r{d.sourceRev}
        </span>
        <span className="text-xs text-gray-500">
          Author so far: {d.authorStats.live} live, {d.authorStats.rejected} rejected · member since {utcDate(d.authorSince)}
        </span>
        {d.description ? (
          <div className="surface-sunken px-3 py-2.5 text-[13.5px] text-gray-400 leading-relaxed whitespace-pre-line mt-1">{d.description}</div>
        ) : (
          <span className="text-[12.5px] text-gray-500">No description.</span>
        )}
        {d.live && (
          <div className="surface-sunken px-3 py-2.5 text-[12.5px] text-gray-400 flex flex-col gap-1 mt-1">
            <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-gray-500">Compared with the live v{d.live.version}</span>
            {d.changes ? (
              <span className="whitespace-pre-line"><span className="text-gray-500">The author says:</span> <span className="text-gray-50">{d.changes}</span></span>
            ) : (
              <span className="text-gray-500">The author did not say what changed.</span>
            )}
            {d.live.title !== d.title && <span>Title: “{d.live.title}” → “{d.title}”</span>}
            {d.live.description !== d.description && <span>The description changed.</span>}
            {d.live.sameRoute ? <span>The route itself is the same.</span> : (
              <span>
                Route: {d.live.summary.steps} → {s.steps} steps, {d.live.summary.points} → {s.points} rail points,
                items {d.live.summary.items.length} → {s.items.length}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="px-5 py-3.5 border-t border-line-faint flex flex-col gap-2.5">
        <span className="panel-label">Steps</span>
        <StepsList maps={s.maps} entry={s.entry} names={s.names} />
        <KillTargets kills={s.kills} names={s.names} />
      </div>

      <div className="px-5 py-3.5 border-t border-line-faint flex flex-col gap-2.5">
        <span className="panel-label">Uses items</span>
        <ItemsBox items={s.items} title={null} names={s.names} />
      </div>

      <div className="px-5 py-3.5 border-t border-line-faint flex flex-col gap-2.5">
        <span className="panel-label">Check it</span>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-glass btn-sm" onClick={() => setShowText(true)}><IconEye className="w-3.5 h-3.5" /> View route text</button>
          <button type="button" className="btn-glass btn-sm" onClick={tryIt}><IconPlay className="w-3.5 h-3.5" /> Try it in my bot</button>
        </div>
        <span className="text-[12.5px] text-gray-500">
          Try it puts a private copy into your Routes, in a <span className="font-mono text-gray-100">Review</span> folder. It does not count as an add.
        </span>
        {[
          ['title', 'The title says what the route does'],
          ['items', 'The item use is clear from the listing'],
          ['tried', 'I tried it in the game'],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2.5 text-[13px] text-gray-400 cursor-pointer">
            <input type="checkbox" className="checkbox" checked={!!checks[k]} onChange={e => setChecks(c => ({ ...c, [k]: e.target.checked }))} />
            {label}
          </label>
        ))}
      </div>

      <div className="px-5 py-3.5 border-t border-line-faint flex flex-col gap-2.5">
        <label htmlFor="rv-reason" className="label mb-0">Reason, if you reject (optional)</label>
        <input id="rv-reason" className="input" maxLength={255} placeholder="e.g. Stops at the second gate"
               value={reason} onChange={e => setReason(e.target.value)} />
        <label className="flex items-center gap-2.5 text-[13px] text-gray-400 cursor-pointer">
          <input type="checkbox" className="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
          Show the reason to {d.author}
        </label>
      </div>

      <div className="px-5 py-3 border-t border-line-faint bg-bar flex flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary btn-sm flex-1" disabled={busy || !d.canApprove} onClick={() => onApprove(d.versionId)}
                  title={!d.canApprove && d.mine ? 'Another reviewer has to approve your own route.' : undefined}>
            <IconCheck className="w-3.5 h-3.5" /> Approve: put it in the library
          </button>
          <button type="button" className="btn-danger btn-sm" disabled={busy || d.status !== 'pending'} onClick={() => onReject(d.versionId, reason, isPublic)}>Reject</button>
        </div>
        <span className="text-xs text-gray-500">
          {d.mine && !d.canApprove ? 'This is your own submission. Another reviewer has to approve it.' : 'Both are logged with your name.'}
        </span>
      </div>

      {showText && (
        <Modal title="Route text" subtitle={`${d.title} · v${d.version}`} size="2xl" onClose={() => setShowText(false)}>
          <pre className="surface-sunken p-3.5 text-[12px] leading-[1.75] text-gray-100 font-mono max-h-[60vh] overflow-auto whitespace-pre">{d.text}</pre>
        </Modal>
      )}
    </aside>
  );
}

// ── REPORTED / LIVE / HIDDEN ──────────────────────────────────────────────────
function ListingTable({ items, kind, busy, canRemove, onHide, onKeep, onUnhide, onRemove }) {
  if (items.length === 0) {
    const t = { reported: 'No open reports.', live: 'Nothing is live yet.', hidden: 'Nothing is hidden.' }[kind];
    return <div className="card"><EmptyState icon={<path d="M20 6 9 17l-5-5" />} title={t} /></div>;
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="card overflow-hidden">
        {items.map(it => (
          <div key={it.listingId} className="list-row px-5 py-3.5 flex flex-wrap items-center gap-x-5 gap-y-2">
            <div className="min-w-0 flex-1 basis-64">
              <b className="block text-sm font-medium text-gray-50 break-words">{it.title}</b>
              <div className="mt-[3px] text-[12.5px] text-gray-500">
                by {it.author}{it.mine ? ' (you)' : ''}
                {kind === 'hidden'
                  ? ` · hidden ${utcDate(it.hiddenAt)}${it.hiddenBy ? ` by ${it.hiddenBy}` : ''}${it.hiddenReason ? ` · “${it.hiddenReason}”` : ''}`
                  : it.approvedBy ? ` · approved ${utcDate(it.approvedAt)} by ${it.approvedBy}` : ''}
                {it.updatePending ? ' · an update is in review' : ''}
              </div>
            </div>
            {kind === 'reported' && (
              <div className="flex flex-wrap gap-1.5 basis-60">
                {it.reasons.map(r => (
                  <span key={r.reason} className="px-2 py-0.5 rounded-md bg-bar border border-line text-[12.5px] text-gray-100">
                    {r.reason}{r.count > 1 && <span className="font-mono text-[11px] text-gray-500 ml-1">{r.count}</span>}
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center gap-4 font-mono text-[12.5px] text-gray-400">
              <span title="Adds">{it.adds} {it.adds === 1 ? 'add' : 'adds'}</span>
              {it.reports > 0 && <span className="text-warn" title="Open reports"><IconFlag className="w-3.5 h-3.5 inline -mt-0.5" /> {it.reports}</span>}
              {it.items?.length > 0 && <span className="text-warn">{itemShort(it.items)}</span>}
              {kind === 'reported' && <span className="text-gray-500">{ago(it.lastReport)}</span>}
            </div>
            <div className="flex gap-1.5 ml-auto">
              {kind === 'hidden' ? (
                <button type="button" className="btn-glass btn-sm" disabled={busy} onClick={() => onUnhide(it)}>Unhide</button>
              ) : (
                <button type="button" className="btn-glass btn-sm" disabled={busy} onClick={() => onHide(it)}>Hide</button>
              )}
              {kind === 'reported' && (
                <button type="button" className="btn-glass btn-sm" disabled={busy} onClick={() => onKeep(it)}>Keep</button>
              )}
              {canRemove && (
                <button type="button" className="btn-danger btn-sm" disabled={busy} onClick={() => onRemove(it)}>Remove</button>
              )}
            </div>
          </div>
        ))}
      </div>
      <span className="text-[13px] text-gray-500">
        {kind === 'reported'
          ? 'Hide takes a listing out of the library at once and can be undone. Keep closes the reports.'
          : 'Hide can be undone.'}
        {canRemove ? ' Remove deletes the listing for good.' : ' Removing a listing for good is for admins and the owner.'}
        {' '}Players who already added a route keep their own copy either way.
      </span>
    </div>
  );
}

function RejectedTable({ items }) {
  if (items.length === 0) {
    return <div className="card"><EmptyState icon={<path d="M20 6 9 17l-5-5" />} title="Nothing was rejected in the last 30 days." /></div>;
  }
  return (
    <div className="card overflow-hidden">
      {items.map(it => (
        <div key={it.versionId} className="list-row px-5 py-3 flex flex-wrap items-center gap-x-5 gap-y-1">
          <div className="min-w-0 flex-1 basis-64">
            <b className="text-sm font-medium text-gray-50 break-words">{it.title} <span className="font-mono text-[11px] font-normal text-gray-500">v{it.version}</span></b>
            <div className="mt-[3px] text-[12.5px] text-gray-500">by {it.author} · rejected {utcDate(it.reviewedAt)}{it.reviewedBy ? ` by ${it.reviewedBy}` : ''}</div>
          </div>
          <span className="text-[13px] text-gray-400 basis-60">
            {it.reason ? `“${it.reason}”` : 'No reason given'}{it.reason && !it.reasonPublic ? ' (not shown to the author)' : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

function HideModal({ target, busy, onClose, onHide }) {
  const [reason, setReason] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  return (
    <Modal
      title="Hide this listing?"
      subtitle={`${target.title} · by ${target.author} · ${target.reports || 0} reports`}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary" disabled={busy} onClick={() => onHide(reason, isPublic)}>Hide listing</button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-400 leading-relaxed">
          No report is needed. The listing leaves the library at once. Players who already added it keep their copy,
          and the author's own route is not touched. Any tester, admin or the owner can unhide it.
        </p>
        <div className="flex flex-col gap-2">
          <label htmlFor="hd-reason" className="label mb-0">Reason (optional)</label>
          <input id="hd-reason" className="input" maxLength={255} value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <label className="flex items-center gap-2.5 text-[13px] text-gray-400 cursor-pointer">
          <input type="checkbox" className="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
          Show the reason to {target.author}
        </label>
        <span className="text-[12.5px] text-gray-500">Written to the audit log with your name.</span>
      </div>
    </Modal>
  );
}
